import React, { useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
interface CircularGaugeProps {
  value: number;
  size?: number;
  strokeWidth?: number;
  isInView: boolean;
  color?: 'primary' | 'secondary';
}
export const CircularGauge = ({
  value,
  size = 100,
  strokeWidth = 10,
  isInView,
  color = 'secondary'
}: CircularGaugeProps) => {
  const controls = useAnimationControls();
  const [displayValue, setDisplayValue] = useState(0);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const startAngle = -140;
  const endAngle = 140;
  const angleRange = endAngle - startAngle;
  const dashArray = angleRange / 360 * circumference;
  const dashOffset = dashArray * (1 - value / 100);
  const rotationOffset = (360 - angleRange) / 2;
  useEffect(() => {
    if (!isInView) {
      setDisplayValue(0);
      controls.set({
        strokeDashoffset: dashArray
      });
      return;
    }
    let animationFrame: number;
    const startTime = Date.now();
    const duration = 1000; // Animation duration
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const currentValue = Math.round(Math.max(0, progress) * value);
      setDisplayValue(currentValue);
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    // Start animations immediately when in view
    controls.start({
      strokeDashoffset: dashOffset,
      transition: {
        duration: 1,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    });
    animationFrame = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [controls, dashArray, dashOffset, value, isInView]);
  return <div style={{
    width: size,
    height: size
  }} data-id="element-194">
      <svg width={size} height={size} style={{
      transform: `rotate(${startAngle + rotationOffset}deg)`
    }} data-id="element-195">
        <defs data-id="element-196">
          <linearGradient id="gauge-gradient-primary" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-197">
            <stop offset="0%" stopColor="#8679DF" data-id="element-198" />
            <stop offset="100%" stopColor="#A89CF4" data-id="element-199" />
          </linearGradient>
          <linearGradient id="gauge-gradient-secondary" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-200">
            <stop offset="0%" stopColor="#9C8EF5" data-id="element-201" />
            <stop offset="100%" stopColor="#80DDDD" data-id="element-202" />
          </linearGradient>
        </defs>
        {/* Background track */}
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E5E7EB" strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={`${dashArray} ${circumference}`} data-id="element-203" />
        {/* Animated fill */}
        <motion.circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={`url(#gauge-gradient-${color})`} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={`${dashArray} ${circumference}`} initial={{
        strokeDashoffset: dashArray
      }} animate={controls} data-id="element-204" />
      </svg>
      {/* Centered Value */}
      <div className="absolute inset-0 flex items-center justify-center font-bold text-3xl text-gray-900" style={{
      fontFeatureSettings: '"tnum" 1'
    }} data-id="element-205">
        {displayValue}
      </div>
    </div>;
};